import React from "react";
import { Box } from "@mui/material";

// US-only phone field that matches the mobile app: a flag + fixed "+1" prefix
// box beside the input, with live "(555) 123-4567" formatting. The `value` it
// holds (and emits via onChange) is the RAW national number — at most 10 digits,
// no formatting. Callers submit E.164 with `+1${value}` (use toE164).
//
// Hand-built (not MUI <TextField>) on purpose: MUI v9 dropped the legacy
// `InputProps` prop, which silently swallowed the flag adornment. This styles a
// plain container with the theme's own input tokens (radius 10, #f9f6ef fill,
// #e8e2d5 border) so it matches every other field AND can't break across MUI
// versions. The 🇺🇸 emoji isn't used because Windows browsers render it as "US".

// Format raw digits as "(555) 123-4567" for display.
export function formatNationalUS(digits) {
  const d = String(digits || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Reduce any stored value (E.164 "+12035551234" or national) to 10 digits.
export function toPhoneDigits(v) {
  const d = String(v || "").replace(/\D/g, "");
  return (d.length === 11 && d.startsWith("1") ? d.slice(1) : d).slice(0, 10);
}

// Convert the raw national value to the E.164 string the API expects (or "").
export function toE164(digits) {
  const d = toPhoneDigits(digits);
  return d ? `+1${d}` : "";
}

const STRIPE = 100 / 13;

// Small, crisp US flag — renders identically on every OS (unlike the emoji).
function UsFlag() {
  return (
    <svg
      width="24"
      height="14"
      viewBox="0 0 190 100"
      style={{
        borderRadius: 2,
        display: "block",
        boxShadow: "0 0 0 0.5px rgba(0,0,0,0.15)",
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <rect width="190" height="100" fill="#B22234" />
      {[1, 3, 5, 7, 9, 11].map((i) => (
        <rect key={i} y={i * STRIPE} width="190" height={STRIPE} fill="#fff" />
      ))}
      <rect width="76" height={7 * STRIPE} fill="#3C3B6E" />
      {[0, 1, 2].map((r) =>
        [0, 1, 2, 3].map((c) => (
          <circle
            key={`${r}-${c}`}
            cx={11 + c * 18}
            cy={9 + r * 16}
            r="2.4"
            fill="#fff"
          />
        )),
      )}
    </svg>
  );
}

export default function UsPhoneField({
  value,
  onChange,
  placeholder = "(555) 123-4567",
  disabled = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#f9f6ef",
        border: "1.5px solid #e8e2d5",
        transition: "border-color 120ms ease",
        "&:hover": { borderColor: disabled ? "#e8e2d5" : "#b8b4a8" },
        "&:focus-within": { borderColor: "#111008" },
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {/* Prefix box — flag + +1, divided from the input (mirrors the mobile app) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: "12px",
          borderRight: "1.5px solid #e8e2d5",
          backgroundColor: "#f2ede2",
          userSelect: "none",
        }}
      >
        <UsFlag />
        <Box
          component="span"
          sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#111008" }}
        >
          +1
        </Box>
      </Box>

      <Box
        component="input"
        type="tel"
        inputMode="tel"
        aria-label="Phone number"
        disabled={disabled}
        placeholder={placeholder}
        value={formatNationalUS(value)}
        onChange={(e) =>
          onChange(e.target.value.replace(/\D/g, "").slice(0, 10))
        }
        sx={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          px: "14px",
          py: "12px",
          fontSize: "0.875rem",
          fontWeight: 500,
          fontFamily: "inherit",
          color: "#111008",
          "&::placeholder": { color: "#b8b4a8", opacity: 1 },
        }}
      />
    </Box>
  );
}
